import React from 'react';
import { Descriptions, Table, Tabs, Tag, Empty, Space, Image, Button, Tooltip } from 'antd';
import EllipsisCell from '../../../../components/common/EllipsisCell';
import PromotionTypeTags from '../../../../components/common/PromotionTypeTags';
import { EnvironmentOutlined, LockOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import OrderStatusFlow from '../../../../components/common/OrderStatusFlow';
import useOrderPiiReveal from '../../../../hooks/useOrderPiiReveal';
import {
  ORDER_STATUS_OPTIONS,
  CHANNEL_OPTIONS,
  SHORTAGE_REASON_OPTIONS,
} from '../../../../constants/order';

/**
 * 订单详情共享内容组件
 * 同时被订单列表 Drawer 和独立详情页路由复用
 *
 * @param {Object} props
 * @param {Object} props.orderDetail - { order, pickingDetails, substituteRecords }
 */
const OrderDetailContent = ({ orderDetail }) => {
  const { t } = useTranslation();

  // buyerPhone 与 deliveryAddress 在库中均为 RSA 密文，默认以掩码/锁图标占位展示。
  // 配送信息区块采用**单一开关**（design D6）：点击标题右上角的眼睛一次调 decrypt-detail，
  // 同时揭示手机号与地址各行，避免为同一份 PII 设置两个语义重叠的开关。
  // Hook 内部已在 orderNo 变化时自动重置为掩码态，无需再额外维护 useEffect。
  const currentOrderNo = orderDetail?.order?.orderNo;
  const {
    revealed: piiRevealed,
    loading: piiLoading,
    phone: revealedPhone,
    delivery,
    toggle: togglePii,
  } = useOrderPiiReveal(currentOrderNo);

  if (!orderDetail) return null;

  // 商品缩略图占位 fallback（与 master-data/item/list 一致）
  const IMAGE_FALLBACK =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==';

  // 统一的商品缩略图渲染（48x48、cover、圆角、点击预览、无图 fallback）
  const renderItemImage = (url) => {
    if (!url) return '-';
    return (
      <Image
        src={url}
        width={48}
        height={48}
        style={{ objectFit: 'cover', borderRadius: 4 }}
        fallback={IMAGE_FALLBACK}
      />
    );
  };

  const order = orderDetail.order || {};
  const orderItems = order.items || [];
  const pickingDetails = orderDetail.pickingDetails || [];
  const pickerName = pickingDetails
    .find((detail) => typeof detail?.pickerName === 'string' && detail.pickerName.trim())
    ?.pickerName.trim() || '-';
  const substituteRecords = orderDetail.substituteRecords || [];

  // 计算当前订单状态节点索引
  const getCurrentStatusIndex = () => {
    if (order.outboundCompleteTime) return 5;
    // IN_DELIVERY 判断必须在 pickingEndTime 之前：配送中订单必然已有拣选结束时间，
    // 若先命中 pickingEndTime 会拦截后续判断导致 In Delivery 节点永远无法高亮；
    // 同时用 orderStatus 兕底（存量数据可能缺失 orderDispatchedTime）
    if (order.orderDispatchedTime || order.orderStatus === 'IN_DELIVERY') return 4;
    if (order.pickingEndTime) return 3;
    if (order.pickingStartTime) return 2;
    if (order.readyToPickTime) return 1;

    switch (order.orderStatus) {
      case 'CREATED':
        return 0;
      case 'IN_PROGRESS':
        return 2;
      case 'COMPLETED':
        return 4;
      case 'IN_DELIVERY':
        return 4;
      case 'RETURN':
        return 5;
      default:
        return 0;
    }
  };

  // 构建状态数据映射
  const buildStatusData = () => {
    const data = {};
    const currentIndex = getCurrentStatusIndex();
    const nodes = [
      { key: 'ordersAccepted', time: order.createTime },
      { key: 'readyToPick', time: order.readyToPickTime },
      { key: 'startPicking', time: order.pickingStartTime },
      { key: 'pickingCompleted', time: order.sortingCompleteTime },
      { key: 'inDelivery', time: order.orderDispatchedTime },
      { key: 'outboundCompleted', time: order.outboundCompleteTime },
    ];
    nodes.forEach((node, index) => {
      const isCompleted = index < currentIndex;
      const hasTime = !!node.time;
      if (isCompleted || hasTime) {
        data[node.key] = { time: node.time || null, completed: isCompleted };
      }
    });
    return data;
  };

  // 配送地址明文由 useOrderPiiReveal 统一解析（未揭示时 delivery 恒为 null）。
  // 注意：**不得**在此对 order.deliveryAddress 做本地 parse 兜底 ——
  // 该字段是整段 RSA 密文，parse 必然失败，回退渲染原始串等于把 684 字符密文
  // 直接泼到界面上（原实现的 catch 分支即如此，已按 design D7 移除）。
  const hasDeliveryAddress = !!order.deliveryAddress;

  /** 未揭示态的地址字段占位：有密文时显示灰色掩码点，本就无地址时显示 '-' */
  const ADDRESS_MASK = '••••••••';
  const renderAddressField = (value) => {
    if (!piiRevealed) {
      return hasDeliveryAddress
        ? <span style={{ color: '#8c8c8c', letterSpacing: '2px' }}>{ADDRESS_MASK}</span>
        : '-';
    }
    return value || '-';
  };

  // 商品明细列
  const itemColumns = [
    { title: t('order.order.detail.items.lineNo'), dataIndex: 'lineNo', width: 180, fixed: 'left', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.items.itemCode'), dataIndex: 'itemCode', width: 150, fixed: 'left', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.items.itemName'), dataIndex: 'itemName', width: 200, fixed: 'left', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.items.originalItemCode'), dataIndex: 'originalItemCode', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v || '-'}</EllipsisCell> },
    { title: t('order.order.detail.items.image'), dataIndex: 'imageUrl', width: 80, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: renderItemImage },
    { title: t('order.order.detail.items.planQty'), dataIndex: 'planQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.items.actualPickedQty'), dataIndex: 'actualPickedQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v ?? '-'}</EllipsisCell> },
    { title: t('order.order.detail.items.unitPrice'), dataIndex: 'unitPrice', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{`฿${v?.toFixed(3) || '0.000'}`}</EllipsisCell> },
    { title: t('order.order.detail.items.finalLineAmount'), dataIndex: 'paidPrice', width: 120, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v != null ? `฿${v.toFixed(3)}` : '-'}</EllipsisCell> },
    { title: t('order.order.detail.items.lineAmount'), dataIndex: 'lineAmount', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{`฿${v?.toFixed(3) || '0.000'}`}</EllipsisCell> },
    {
      title: t('order.order.detail.items.isFreebie'),
      dataIndex: 'isFreebie',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    { title: t('order.order.detail.items.promotion'), dataIndex: 'promotionType', width: 120, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <PromotionTypeTags value={v} /> },
    { title: t('order.order.detail.items.returnQty'), dataIndex: 'returnQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v ?? '-'}</EllipsisCell> },
    { title: t('order.order.detail.items.remark'), dataIndex: 'remark', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v || '-'}</EllipsisCell> },
  ];

  // 拣选明细列
  const pickingColumns = [
    { title: t('order.order.detail.picking.itemCode'), dataIndex: 'itemCode', width: 150, fixed: 'left', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.picking.itemName'), dataIndex: 'itemName', width: 200, fixed: 'left', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.picking.image'), dataIndex: 'imageUrl', width: 80, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: renderItemImage },
    { title: t('order.order.detail.picking.planQty'), dataIndex: 'planQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.picking.actualPickedQty'), dataIndex: 'actualQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    {
      title: t('order.order.detail.picking.isCancelled'),
      dataIndex: 'isCancelled',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'default' : 'green'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.isRepicked'),
      dataIndex: 'isRepicked',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'blue' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.isSubstituted'),
      dataIndex: 'isSubstituted',
      width: 130,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'orange' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.isConfirmShort'),
      dataIndex: 'isConfirmShort',
      width: 150,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'red' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.shortageReason'),
      dataIndex: 'shortageReason',
      width: 120,
      ellipsis: { showTitle: false },
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (reason) => {
        if (!reason) return <EllipsisCell>{'-'}</EllipsisCell>;
        const option = SHORTAGE_REASON_OPTIONS.find((opt) => opt.value === reason);
        const displayText = t(option?.label || reason);
        return <EllipsisCell>{displayText}</EllipsisCell>;
      },
    },
    { title: t('order.order.detail.picking.pickTime'), dataIndex: 'pickTime', width: 180, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => <EllipsisCell>{v ? dayjs(v).format('DD-MM-YYYY HH:mm:ss') : '-'}</EllipsisCell> },
  ];

  // 替换明细列表头：过长时显示省略号并悬浮展示完整列名
  const substituteHeaderCell = (key) => () => ({
    style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    title: t(key),
  });

  // 替换明细列
  const substituteColumns = [
    { title: t('order.order.detail.substitute.originalItemCode'), dataIndex: 'originalItemCode', width: 150, fixed: 'left', onHeaderCell: substituteHeaderCell('order.order.detail.substitute.originalItemCode'), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.originalItemName'), dataIndex: 'originalItemName', width: 200, fixed: 'left', onHeaderCell: substituteHeaderCell('order.order.detail.substitute.originalItemName'), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.originalImage'), dataIndex: 'originalImageUrl', width: 130, onHeaderCell: substituteHeaderCell('order.order.detail.substitute.originalImage'), render: renderItemImage },
    { title: t('order.order.detail.substitute.originalQty'), dataIndex: 'originalQty', width: 100, align: 'right', onHeaderCell: substituteHeaderCell('order.order.detail.substitute.originalQty'), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.originalPrice'), dataIndex: 'originalPrice', width: 130, align: 'right', onHeaderCell: substituteHeaderCell('order.order.detail.substitute.originalPrice'), render: (v) => <EllipsisCell>{`฿${v?.toFixed(3) || '0.000'}`}</EllipsisCell> },
    { title: t('order.order.detail.substitute.substituteItemCode'), dataIndex: 'substituteItemCode', width: 150, onHeaderCell: substituteHeaderCell('order.order.detail.substitute.substituteItemCode'), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.substituteItemName'), dataIndex: 'substituteItemName', width: 200, onHeaderCell: substituteHeaderCell('order.order.detail.substitute.substituteItemName'), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.substituteImage'), dataIndex: 'substituteImageUrl', width: 130, onHeaderCell: substituteHeaderCell('order.order.detail.substitute.substituteImage'), render: renderItemImage },
    { title: t('order.order.detail.substitute.substituteQty'), dataIndex: 'substituteQty', width: 130, align: 'right', onHeaderCell: substituteHeaderCell('order.order.detail.substitute.substituteQty'), render: (v) => <EllipsisCell>{v}</EllipsisCell> },
    { title: t('order.order.detail.substitute.substitutePrice'), dataIndex: 'substitutePrice', width: 130, align: 'right', onHeaderCell: substituteHeaderCell('order.order.detail.substitute.substitutePrice'), render: (v) => <EllipsisCell>{`฿${v?.toFixed(3) || '0.000'}`}</EllipsisCell> },
    {
      title: t('order.order.detail.substitute.priceDiff'),
      dataIndex: 'priceDiff',
      width: 100,
      align: 'right',
      onHeaderCell: substituteHeaderCell('order.order.detail.substitute.priceDiff'),
      render: (diff) => (
        <span style={{ color: diff > 0 ? '#ff4d4f' : diff < 0 ? '#52c41a' : '#000' }}>
          <EllipsisCell>฿{diff?.toFixed(3) || '0.000'}</EllipsisCell>
        </span>
      ),
    },
    { title: t('order.order.detail.substitute.substituteTime'), dataIndex: 'substituteTime', width: 180, onHeaderCell: substituteHeaderCell('order.order.detail.substitute.substituteTime'), render: (v) => <EllipsisCell>{v ? dayjs(v).format('DD-MM-YYYY HH:mm:ss') : '-'}</EllipsisCell> },
  ];

  return (
    <>
      {/* 订单状态流程 */}
      <OrderStatusFlow
        current={getCurrentStatusIndex()}
        statusData={buildStatusData()}
        isCancelled={order.orderStatus === 'CANCELLED'}
        cancelTime={order.cancelTime}
        isReturned={order.orderStatus === 'RETURN'}
        returnTime={order.returnTime}
      />

      {/* 基本信息 */}
      <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('order.order.detail.basic.orderNo')}>
          {order.orderNo || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.upstreamOrderNo')}>
          {order.upstreamOrderNo || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.orderType')}>
          {order.orderType || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.channel')}>
          {(() => {
            const option = CHANNEL_OPTIONS.find((opt) => opt.value === order.channel);
            return option ? t(option.label) : (order.channel || '-');
          })()}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.orderStatus')}>
          <Tag color={ORDER_STATUS_OPTIONS.find((opt) => opt.value === order.orderStatus)?.color}>
            {t(ORDER_STATUS_OPTIONS.find((opt) => opt.value === order.orderStatus)?.label || order.orderStatus)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.storeCode')}>
          {order.storeCode || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.skuQty')}>
          {order.skuQty || 0}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.totalMu')}>
          {order.totalMu || 0}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.totalAmount')}>
          ฿{order.subTotal?.toFixed(3) || '0.000'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.grandTotal')}>
          {order.grandTotal != null ? `฿${order.grandTotal.toFixed(3)}` : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.cancelTime')} span={2}>
          {order.cancelTime ? dayjs(order.cancelTime).format('DD-MM-YYYY HH:mm:ss') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.createTime')}>
          {order.createTime ? dayjs(order.createTime).format('DD-MM-YYYY HH:mm:ss') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.picking.pickerName')}>
          {pickerName}
        </Descriptions.Item>
      </Descriptions>

      {/* Delivery Info */}
      <Descriptions
        bordered
        column={4}
        size="small"
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{t('order.order.detail.delivery.title')}</span>
            <Tooltip title={piiRevealed ? t('order.order.detail.delivery.hidePlaintext') : t('order.order.detail.delivery.viewPlaintext')}>
              <Button
                type="text"
                size="small"
                loading={piiLoading}
                icon={piiRevealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={togglePii}
              />
            </Tooltip>
          </div>
        }
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label={t('order.order.detail.delivery.recipientName')}>
          {/* buyerName 本身不加密，未揭示态直接展示即可；揭示后优先用地址内的 firstName + lastName */}
          {piiRevealed && delivery
            ? [delivery.firstName, delivery.lastName].filter(Boolean).join(' ') || order.buyerName || '-'
            : order.buyerName || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.phoneNumber')}>
          {piiRevealed ? (
            revealedPhone || '-'
          ) : (
            <span style={{ color: '#8c8c8c', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <LockOutlined />
              {t('order.order.detail.delivery.encrypted')}
            </span>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.note')}>
          {renderAddressField(delivery?.deliveryNote)}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.address')}>
          {renderAddressField(
            [delivery?.addressLine1, delivery?.addressLine2, delivery?.addressLine3]
              .filter(Boolean)
              .join(', ')
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.subDistrict')}>
          {renderAddressField(delivery?.subDistrict)}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.district')}>
          {renderAddressField(delivery?.city)}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.province')}>
          {renderAddressField(delivery?.state)}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.country')}>
          {renderAddressField(delivery?.country)}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.postcode')}>
          {renderAddressField(delivery?.postalCode)}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.riderName')}>
          {order.riderName || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.latLng')} span={2}>
          {/* 未揭示态拿不到明文坐标，因此**不渲染 Google Maps 链接**，只给掩码占位 */}
          {piiRevealed && delivery?.latitude && delivery?.longitude ? (
            <a
              href={`https://www.google.com/maps?q=${delivery.latitude},${delivery.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#13c2c2' }}
            >
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              {delivery.latitude}, {delivery.longitude}
            </a>
          ) : renderAddressField(null)}
        </Descriptions.Item>
      </Descriptions>

      {/* 三个 Tab */}
      <Tabs
        defaultActiveKey="1"
        type="card"
        items={[
          {
            key: '1',
            label: t('order.order.detail.tabs.orderDetails'),
            children: (
              <Table
                dataSource={orderItems}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 1740, y: 400 }}
                locale={{ emptyText: <Empty description={t('common.message.noData')} /> }}
                columns={itemColumns}
              />
            ),
          },
          {
            key: '2',
            label: t('order.order.detail.tabs.pickingDetails'),
            children: (
              <div>
                <Table
                  dataSource={pickingDetails}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  scroll={{ x: 1660, y: 400 }}
                  locale={{ emptyText: <Empty description={t('order.order.detail.picking.emptyText')} /> }}
                  columns={pickingColumns}
                />
                {pickingDetails.length > 0 && (
                  <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                    <Space>
                      <span>
                        {t('order.order.detail.picking.summaryPicked')}:{' '}
                        {pickingDetails.filter((item) => !item.isCancelled && item.actualQty > 0).length}
                      </span>
                      <span>|</span>
                      <span>
                        {t('order.order.detail.picking.summaryTotal')}: {pickingDetails.length}
                      </span>
                      <span>|</span>
                      <span>
                        {t('order.order.detail.picking.summaryRate')}:{' '}
                        {(
                          (pickingDetails.filter((item) => !item.isCancelled && item.actualQty > 0).length /
                            pickingDetails.length) *
                          100
                        ).toFixed(1)}
                        %
                      </span>
                    </Space>
                  </div>
                )}
              </div>
            ),
          },
          {
            key: '3',
            label: t('order.order.detail.tabs.substituteDetail'),
            children: (
              <Table
                dataSource={substituteRecords}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 1730, y: 400 }}
                locale={{ emptyText: <Empty description={t('common.message.noData')} /> }}
                columns={substituteColumns}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default OrderDetailContent;
